import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, Heart, Share, Trash2, Play, Grid3X3, Square, Video, Image as ImageIcon } from "lucide-react";
import { useQuery } from "convex/react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { api } from "../../../convex/_generated/api";
import type { Photo, Album } from "../../types";

interface PhotosAppProps {
  onClose: () => void;
}

export function PhotosApp({ onClose }: PhotosAppProps) {
  const [selectedTab, setSelectedTab] = useState<"library" | "albums" | "favorites">("library");
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isGridView, setIsGridView] = useState(true);

  // Fetch photos from Convex
  const photos = useQuery(api.photos.getAll) || [];
  const albums = useQuery(api.photos.getAlbums) || [];
  const favoritePhotos = photos.filter((p) => p.isFavorite);

  const filteredPhotos =
    selectedTab === "favorites" ? favoritePhotos : photos;

  // Group photos by date for "library" view
  const groupedByDate = filteredPhotos.reduce((acc, photo) => {
    const date = new Date(photo.createdAt).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    if (!acc[date]) acc[date] = [];
    acc[date].push(photo);
    return acc;
  }, {} as Record<string, Photo[]>);

  const handlePhotoClick = (photo: Photo, index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleFavorite = async (photoId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: Implement favorite toggle
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="absolute inset-0 z-40 flex flex-col bg-black"
    >
      {/* Status Bar */}
      <div className="flex items-center justify-between px-6 pt-3 text-white">
        <span className="text-sm font-semibold">9:41</span>
        <div className="flex items-center gap-1">
          <div className="h-4 w-4 rounded-full border-2 border-white" />
          <div className="h-4 w-4 rounded-full border-2 border-white" />
          <div className="h-2 w-6 rounded-sm border-2 border-white bg-white" />
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="flex items-center justify-between px-4 pt-2 pb-4">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={onClose}
          className="flex items-center gap-1 text-ios-blue"
        >
          <ChevronLeft className="h-6 w-6" />
          <span className="text-lg">Memories</span>
        </motion.button>

        <h1 className="text-lg font-semibold text-white">
          {selectedTab === "library" && "All Photos"}
          {selectedTab === "albums" && "Albums"}
          {selectedTab === "favorites" && "Favorites"}
        </h1>

        <div className="flex items-center gap-2">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsGridView(!isGridView)}
            className="p-2 text-white"
          >
            {isGridView ? <Grid3X3 className="h-5 w-5" /> : <Square className="h-5 w-5" />}
          </motion.button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex items-center justify-around border-b border-white/10 px-4 pb-2">
        {["library", "albums", "favorites"].map((tab) => (
          <motion.button
            key={tab}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSelectedTab(tab as typeof selectedTab)}
            className={`relative px-4 py-2 text-sm font-medium capitalize transition-colors ${
              selectedTab === tab ? "text-white" : "text-white/50"
            }`}
          >
            {tab}
            {selectedTab === tab && (
              <motion.div
                layoutId="photosTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-ios-blue"
              />
            )}
          </motion.button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {selectedTab === "library" && (
          <div className="p-2">
            {Object.entries(groupedByDate).map(([date, datePhotos]) => (
              <div key={date} className="mb-4">
                <h3 className="mb-2 px-2 text-sm font-semibold text-white">
                  {date}
                </h3>
                <div className="grid grid-cols-3 gap-1">
                  {datePhotos.map((photo, index) => (
                    <motion.div
                      key={photo._id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.02 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handlePhotoClick(photo, index)}
                      className="relative aspect-square cursor-pointer overflow-hidden bg-gray-800"
                    >
                      <img
                        src={photo.thumbnailUrl || photo.url}
                        alt={photo.filename}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                      {photo.isFavorite && (
                        <div className="absolute right-1 top-1 rounded-full bg-white/80 p-1">
                          <Heart className="h-3 w-3 fill-red-500 text-red-500" />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedTab === "albums" && (
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              {albums.map((album, index) => (
                <motion.div
                  key={album._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="cursor-pointer"
                >
                  <div className="aspect-square overflow-hidden rounded-lg bg-gray-800">
                    {album.coverPhotoId ? (
                      <img
                        src={photos.find((p) => p._id === album.coverPhotoId)?.thumbnailUrl}
                        alt={album.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-white/30">
                        <ImageIcon className="h-12 w-12" />
                      </div>
                    )}
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-white">
                    {album.name}
                  </h3>
                  <p className="text-xs text-white/50">{album.photoCount} items</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {selectedTab === "favorites" && (
          <div className="p-2">
            <div className="grid grid-cols-3 gap-1">
              {favoritePhotos.map((photo, index) => (
                <motion.div
                  key={photo._id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.02 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handlePhotoClick(photo, index)}
                  className="relative aspect-square cursor-pointer overflow-hidden bg-gray-800"
                >
                  <img
                    src={photo.thumbnailUrl || photo.url}
                    alt={photo.filename}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute right-1 top-1 rounded-full bg-white/80 p-1">
                    <Heart className="h-3 w-3 fill-red-500 text-red-500" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={filteredPhotos.map((photo) => ({
          src: photo.url,
          alt: photo.filename,
        }))}
      />

      {/* Home Indicator */}
      <div className="pb-2 pt-1">
        <div className="mx-auto h-1 w-32 rounded-full bg-white/30" />
      </div>
    </motion.div>
  );
}